import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardMedia,
  Typography,
  Chip,
  IconButton,
  Skeleton,
  Alert,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

interface MediaItem {
  id: string;
  filename: string;
  originalName: string;
  fileType: string;
  storageUrl: string;
  uploadedAt: string;
  featuredDate?: string;
  uploaderName?: string;
}

interface FeaturedMediaDisplayProps {
  context: 'landing' | 'guest';
  autoRotate?: boolean;
  rotationInterval?: number; // in milliseconds
  maxItems?: number;
  showUploader?: boolean;
  onMediaClick?: (media: MediaItem) => void;
  className?: string;
}

export const FeaturedMediaDisplay: React.FC<FeaturedMediaDisplayProps> = ({
  context = 'guest',
  autoRotate = true,
  rotationInterval = 5000,
  maxItems = 3,
  showUploader = true,
  onMediaClick,
  className
}) => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(autoRotate);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch featured media
  const fetchFeaturedMedia = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to fetch from API first
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
        const response = await fetch(`${API_BASE_URL}/media/featured?context=${context}`);
        const data = await response.json();
        
        if (data.success) {
          setMediaItems(data.data.media.slice(0, maxItems));
          return;
        }
      } catch (apiError) {
        console.log('API not available, using demo media');
      }
      
      // Fallback to demo media when API is not available
      const demoMedia: MediaItem[] = [
        {
          id: 'demo-1',
          filename: 'birthday-cake.svg',
          originalName: 'Birthday Cake',
          fileType: 'image/svg+xml',
          storageUrl: '/sample-media/birthday-cake.svg',
          uploadedAt: new Date().toISOString(),
          featuredDate: new Date().toISOString(),
          uploaderName: 'Sarah'
        },
        {
          id: 'demo-2',
          filename: 'party-balloons.svg',
          originalName: 'Party Balloons',
          fileType: 'image/svg+xml',
          storageUrl: '/sample-media/party-balloons.svg',
          uploadedAt: new Date().toISOString(),
          featuredDate: new Date().toISOString(),
          uploaderName: 'Mike'
        },
        {
          id: 'demo-3',
          filename: 'party-decorations.svg',
          originalName: 'Party Decorations',
          fileType: 'image/svg+xml',
          storageUrl: '/sample-media/party-decorations.svg',
          uploadedAt: new Date().toISOString(),
          featuredDate: new Date().toISOString(),
          uploaderName: 'Emma'
        },
        {
          id: 'demo-4',
          filename: 'gift-boxes.svg',
          originalName: 'Gift Boxes',
          fileType: 'image/svg+xml',
          storageUrl: '/sample-media/gift-boxes.svg',
          uploadedAt: new Date().toISOString(),
          featuredDate: new Date().toISOString(),
          uploaderName: 'John'
        },
        {
          id: 'demo-5',
          filename: 'party-food.svg',
          originalName: 'Party Food',
          fileType: 'image/svg+xml',
          storageUrl: '/sample-media/party-food.svg',
          uploadedAt: new Date().toISOString(),
          featuredDate: new Date().toISOString(),
          uploaderName: 'Lisa'
        }
      ];
      
      setMediaItems(demoMedia.slice(0, maxItems));
      
    } catch (err) {
      setError('Failed to load media');
      console.error('Error fetching featured media:', err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-rotation effect
  useEffect(() => {
    if (!isPlaying || mediaItems.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % mediaItems.length);
    }, rotationInterval);

    return () => clearInterval(interval);
  }, [isPlaying, mediaItems.length, rotationInterval]);

  // Initial load
  useEffect(() => {
    fetchFeaturedMedia();
  }, [context]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleRefresh = () => {
    fetchFeaturedMedia();
  };

  const handleMediaClick = (media: MediaItem) => {
    if (onMediaClick) {
      onMediaClick(media);
    }
  };

  const isVideo = (fileType: string) => fileType.startsWith('video/');

  if (loading) {
    return (
      <Box className={className}>
        <Skeleton 
          variant="rectangular" 
          width="100%" 
          height={isMobile ? 200 : 300}
          sx={{ borderRadius: 2 }}
        />
        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="30%" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <IconButton size="small" onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        }
        className={className}
      >
        {error}
      </Alert>
    );
  }

  if (mediaItems.length === 0) {
    return (
      <Box 
        className={className}
        sx={{ 
          textAlign: 'center', 
          py: 4,
          color: 'text.secondary'
        }}
      >
        <Typography variant="h6" gutterBottom>
          No Featured Media
        </Typography>
        <Typography variant="body2">
          {context === 'landing' 
            ? 'Featured photos will appear here once guests start sharing memories!'
            : 'No media has been featured yet. Check back later!'
          }
        </Typography>
      </Box>
    );
  }

  const currentMedia = mediaItems[currentIndex];

  return (
    <Box className={className}>
      <Card 
        sx={{ 
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          cursor: onMediaClick ? 'pointer' : 'default'
        }}
        onClick={() => handleMediaClick(currentMedia)}
      >
        <Fade key={currentMedia.id} in timeout={500}>
          <Box>
            {isVideo(currentMedia.fileType) ? (
              <CardMedia
                component="video"
                height={isMobile ? 200 : 300}
                src={currentMedia.storageUrl}
                controls={false}
                autoPlay
                muted
                loop
                sx={{ objectFit: 'cover' }}
              />
            ) : (
              <CardMedia
                component="img"
                height={isMobile ? 200 : 300}
                image={currentMedia.storageUrl}
                alt={currentMedia.originalName}
                sx={{ objectFit: 'cover' }}
              />
            )}
          </Box>
        </Fade>

        {/* Media controls overlay */}
        {mediaItems.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              gap: 1
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handlePlayPause();
              }}
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleRefresh();
              }}
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        )}

        {/* Media info overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
            color: 'white',
            p: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {showUploader && currentMedia.uploaderName && (
              <Chip
                label={`By ${currentMedia.uploaderName}`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontSize: '0.75rem'
                }}
              />
            )}
            {isVideo(currentMedia.fileType) && (
              <Chip
                label="Video"
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontSize: '0.75rem'
                }}
              />
            )}
            {mediaItems.length > 1 && (
              <Typography variant="caption" sx={{ ml: 'auto' }}>
                {currentIndex + 1} of {mediaItems.length}
              </Typography>
            )}
          </Box>
        </Box>
      </Card>

      {/* Pagination dots */}
      {mediaItems.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            mt: 2
          }}
        >
          {mediaItems.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentIndex(index)}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: index === currentIndex ? 'primary.main' : 'grey.300',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                '&:hover': {
                  backgroundColor: index === currentIndex ? 'primary.dark' : 'grey.400'
                }
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FeaturedMediaDisplay;